import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getFirestore, onSnapshot } from 'firebase/firestore';
import { parseChatState, V2_CHAT_TAB } from '../utils/calendarV2Persist';
import { latestChatPreview, tourChatInboxThreadFromDoc } from '../utils/tourChatNotify';

function threadFromChatEntry(id, data = {}) {
  const tourId = String(data.tourId || '').trim()
    || String(id || '').split('__')[0];
  const parsed = parseChatState(data.text || data.publishedText || data.draftText || '');
  const preview = latestChatPreview(parsed.channels);
  if (!preview) {
    return tourChatInboxThreadFromDoc({
      tourId,
      tourName: data.tourName || '',
      channelId: 'party',
    }, tourId);
  }
  return tourChatInboxThreadFromDoc({
    tourId,
    tourName: data.tourName || '',
    channelId: preview.channelId,
    lastText: preview.text,
    lastWho: preview.who,
    lastWhoId: preview.uid,
    lastAt: preview.at,
  }, tourId);
}

export function useTourChatInbox(uid, enabled) {
  const [inboxRows, setInboxRows] = useState([]);
  const [ownChatRows, setOwnChatRows] = useState([]);
  const [folderNames, setFolderNames] = useState({});

  useEffect(() => {
    if (!enabled || !uid) {
      setInboxRows([]);
      return undefined;
    }
    const db = getFirestore();
    return onSnapshot(
      collection(db, 'users', uid, 'tourChatInbox'),
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const thread = tourChatInboxThreadFromDoc(d.data() || {}, d.id);
          if (thread) rows.push(thread);
        });
        setInboxRows(rows);
      },
      (err) => {
        console.warn('tourChatInbox snapshot', err);
        setInboxRows([]);
      }
    );
  }, [uid, enabled]);

  useEffect(() => {
    if (!enabled || !uid) {
      setFolderNames({});
      return undefined;
    }
    let cancelled = false;
    const db = getFirestore();
    getDoc(doc(db, 'users', uid))
      .then((snap) => {
        if (cancelled) return;
        const folders = snap.data()?.folders || {};
        const names = {};
        Object.entries(folders).forEach(([id, folder]) => {
          names[id] = String(folder?.name || folder?.tour?.name || '').trim();
        });
        setFolderNames(names);
      })
      .catch(() => {
        if (!cancelled) setFolderNames({});
      });
    return () => {
      cancelled = true;
    };
  }, [uid, enabled]);

  useEffect(() => {
    if (!enabled || !uid) {
      setOwnChatRows([]);
      return undefined;
    }
    const db = getFirestore();
    return onSnapshot(
      collection(db, 'users', uid, 'calendarTabEntries'),
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const data = d.data() || {};
          if (String(data.tabKey || '') !== V2_CHAT_TAB && !String(d.id).includes('__CHAT__')) {
            return;
          }
          const thread = threadFromChatEntry(d.id, data);
          if (thread) rows.push(thread);
        });
        setOwnChatRows(rows);
      },
      (err) => {
        console.warn('tour chat tab scan', err);
        setOwnChatRows([]);
      }
    );
  }, [uid, enabled]);

  return useMemo(() => {
    const byTour = new Map();
    Object.entries(folderNames).forEach(([tourId, tourName]) => {
      if (!tourId || !tourName) return;
      byTour.set(tourId, tourChatInboxThreadFromDoc({
        tourId,
        tourName,
        channelId: 'party',
      }));
    });
    [...ownChatRows, ...inboxRows].forEach((row) => {
      const named = folderNames[row.tourId]
        ? {
            ...row,
            tourName: folderNames[row.tourId],
            name: `${folderNames[row.tourId]} · ${String(row.name || '').split(' · ').slice(-1)[0]}`,
          }
        : row;
      const prev = byTour.get(row.tourId);
      const nextAt = Date.parse(String(named.lastMessage?.timestamp || '')) || 0;
      const prevAt = Date.parse(String(prev?.lastMessage?.timestamp || '')) || 0;
      if (!prev || nextAt >= prevAt) byTour.set(row.tourId, named);
    });
    return [...byTour.values()].sort((a, b) => {
      const aAt = Date.parse(String(a.lastMessage?.timestamp || '')) || 0;
      const bAt = Date.parse(String(b.lastMessage?.timestamp || '')) || 0;
      return bAt - aAt;
    });
  }, [inboxRows, ownChatRows, folderNames]);
}
